import { AlertTriangle, CheckCircle, Info, X, Navigation } from 'lucide-react';
import { useEditorStore } from '../../store/editorStore';

export default function ValidationPanel() {
  const validationResult = useEditorStore((s) => s.validationResult);
  const toggleValidationPanel = useEditorStore((s) => s.toggleValidationPanel);
  const setActivePage = useEditorStore((s) => s.setActivePage);
  const selectLayer = useEditorStore((s) => s.selectLayer);
  const story = useEditorStore((s) => s.story);

  if (!validationResult) {
    return (
      <div className="rounded-xl shadow-2xl p-4" style={{ background: 'var(--t-surface)', border: '1px solid var(--t-border)' }}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold" style={{ color: 'var(--t-text2)' }}>Validation</h3>
          <button onClick={toggleValidationPanel} className="icon-btn w-6 h-6"><X size={14} /></button>
        </div>
        <p className="text-xs" style={{ color: 'var(--t-muted)' }}>Run validation from the toolbar</p>
      </div>
    );
  }

  const { valid, issues, errorCount, warningCount } = validationResult;

  function navigateToIssue(issue) {
    if (issue.pageId) {
      setActivePage(issue.pageId);
    }
    if (issue.layerId) {
      selectLayer(issue.layerId);
    }
  }

  const errors = issues.filter((i) => i.severity === 'error');
  const warnings = issues.filter((i) => i.severity === 'warning');
  const infos = issues.filter((i) => i.severity === 'info');

  return (
    <div className="rounded-xl shadow-2xl max-h-[70vh] flex flex-col overflow-hidden"
         style={{ background: 'var(--t-surface)', border: '1px solid var(--t-border)' }}>
      {/* Header */}
      <div className="flex items-center justify-between p-3" style={{ borderBottom: '1px solid var(--t-border)' }}>
        <div className="flex items-center gap-2">
          {valid ? (
            <CheckCircle size={16} className="text-green-500" />
          ) : (
            <AlertTriangle size={16} className="text-yellow-500" />
          )}
          <h3 className="text-sm font-semibold" style={{ color: 'var(--t-text2)' }}>
            {valid ? 'AMP Valid ✓' : 'Validation Issues'}
          </h3>
        </div>
        <button onClick={toggleValidationPanel} className="icon-btn w-6 h-6"><X size={14} /></button>
      </div>

      {/* Stats */}
      <div className="flex gap-3 p-3" style={{ borderBottom: '1px solid var(--t-border)' }}>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-red-500" />
          <span className="text-xs" style={{ color: 'var(--t-muted)' }}>{errorCount} errors</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-yellow-500" />
          <span className="text-xs" style={{ color: 'var(--t-muted)' }}>{warningCount} warnings</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-blue-500" />
          <span className="text-xs" style={{ color: 'var(--t-muted)' }}>{infos.length} info</span>
        </div>
      </div>

      {/* Issues list */}
      <div className="flex-1 overflow-y-auto divide-y" style={{ borderColor: 'var(--t-border)' }}>
        {issues.length === 0 ? (
          <div className="p-4 text-center">
            <CheckCircle size={24} className="text-green-500 mx-auto mb-2" />
            <p className="text-sm text-green-400 font-medium">Story is AMP-valid!</p>
            <p className="text-xs mt-1" style={{ color: 'var(--t-muted)' }}>Ready to export and publish.</p>
          </div>
        ) : (
          [...errors, ...warnings, ...infos].map((issue) => (
            <IssueRow
              key={issue.id}
              issue={issue}
              onNavigate={() => navigateToIssue(issue)}
              story={story}
            />
          ))
        )}
      </div>
    </div>
  );
}

function IssueRow({ issue, onNavigate, story }) {
  const pageLabel = issue.pageIndex !== undefined
    ? `Page ${issue.pageIndex + 1}`
    : '';

  return (
    <div
      className={`p-3 transition-colors
        ${issue.severity === 'error' ? 'border-l-2 border-l-red-500' :
          issue.severity === 'warning' ? 'border-l-2 border-l-yellow-500' :
          'border-l-2 border-l-blue-500'}`}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--t-hover)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = ''; }}
    >
      <div className="flex items-start gap-2">
        {issue.severity === 'error' ? (
          <AlertTriangle size={13} className="text-red-400 mt-0.5 flex-shrink-0" />
        ) : issue.severity === 'warning' ? (
          <AlertTriangle size={13} className="text-yellow-400 mt-0.5 flex-shrink-0" />
        ) : (
          <Info size={13} className="text-blue-400 mt-0.5 flex-shrink-0" />
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {pageLabel && (
              <span className="text-xs px-1.5 py-0.5 rounded flex-shrink-0"
                    style={{ background: 'var(--t-panel)', color: 'var(--t-muted)' }}>
                {pageLabel}
              </span>
            )}
            <p className="text-xs" style={{ color: 'var(--t-text2)' }}>{issue.message}</p>
          </div>

          {issue.suggestion && (
            <p className="text-xs mt-1" style={{ color: 'var(--t-muted)' }}>{issue.suggestion}</p>
          )}

          {(issue.pageId || issue.layerId) && (
            <button
              onClick={onNavigate}
              className="flex items-center gap-1 mt-1 text-xs text-indigo-400 hover:text-indigo-300"
            >
              <Navigation size={10} />
              Go to element
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
